<script setup lang="ts">
import {
  BadRequestError,
  type DemoRequestRegister,
  type UserRegister,
  type ValidationErrorMessage,
} from "@gasparigit/omnia-sdk";
import { z } from "zod";
import {
  alertToast,
  processError,
  requiredError,
  successToast,
} from "~/lib/utils/errors";
import type { CreateLinkConfig } from "~/utils/types";
import AdministrationUnit from "../fields/AdministrationUnit.vue";
import AreasField from "../fields/Areas.vue";
import CheckboxPreference from "../fields/CheckboxPreference.vue";
import {
  AutoFormField,
  type ConfigItem,
  type FieldProps,
} from "../ui/auto-form";
import { createDemoRequest } from "~/lib/utils/forms";
import { TriangleAlert } from "lucide-vue-next";

const omniaSdk = useOmniaSdk();

const open = defineModel<boolean>("open");
const props = defineProps<{
  linkConfig?: CreateLinkConfig;
}>();
const loading = ref<boolean>(false);

const emit = defineEmits<{
  (e: "requestLogin"): void;
}>();

const { data: areas } = useAsyncData(`all-areas`, async () => {
  try {
    return await omniaSdk.v1.areas.list({ pagination: "none" });
  } catch (error) {
    processError(error);
  }
});

const emailAlreadyExists = ref<boolean>(false);

async function checkEmailExists(email: string) {
  try {
    emailAlreadyExists.value = await omniaSdk.auth.user.checkEmail(email);
  } catch (error) {
    console.log(error);
  }
}

const formSchema = z
  .object({
    name: z.string(requiredError).describe("Nome"),
    surname: z.string(requiredError).describe("Cognome"),
    email: z
      .string(requiredError)
      .trim()
      .email("Email non valida")
      .describe("Email"),
    email_confirmation: z
      .string(requiredError)
      .trim()
      .email("Email non valida")
      .describe("Conferma email"),
    phone: z.string().describe("Telefono").optional(),
    ipa_code: z.string(requiredError).describe("Ente"),
    user_areas: z
      .array(z.string(), requiredError)
      .min(1, "Seleziona almeno un'area di appartenenza")
      .describe("Aree di appartenenza"),
    newsletter_accepted: z.boolean().optional(),
    user_policy_accepted: z.boolean(requiredError).refine((value) => value, {
      message: "Devi accettare l'informativa sulla privacy.",
      path: ["user_policy_accepted"],
    }),
  })
  .refine((data) => data.email === data.email_confirmation, {
    message: "Email e conferma email devono corrispondere",
    path: ["email_confirmation"],
  });

const fieldConfig: Record<string, ConfigItem> = {
  name: {
    inputProps: { autocomplete: "given-name" },
  },
  surname: {
    inputProps: { autocomplete: "family-name" },
  },
  email: {
    inputProps: { autocomplete: "email" },
  },
  email_confirmation: {
    inputProps: { autocomplete: "email" },
  },
  phone: {
    inputProps: { autocomplete: "tel" },
  },
  ipa_code: {
    label: "Ente",
    component: AdministrationUnit,
  },
  note: {
    component: "textarea",
  },
  newsletter_accepted: {
    label: "Ricevi la newsletter delle novità Gaspari",
    component: CheckboxPreference,
  },
  user_policy_accepted: {
    label: "privacy_policy",
    component: CheckboxPreference,
  },
  user_areas: {
    label: "Aree di appartenenza",
    component: defineComponent(
      (props: FieldProps) => () =>
        h(AreasField, {
          areas: areas.value.data,
          ...props,
        })
    ),
  },
};

const form = useForm({
  validationSchema: toTypedSchema(formSchema),
});

watch(
  () => form.values.email,
  async (email) => {
    if (email && z.string().email().safeParse(email).success) {
      await checkEmailExists(email);
    } else {
      emailAlreadyExists.value = false;
    }
  }
);

const captchaToken = ref<string>();

async function onSubmit(values: Record<string, unknown>) {
  loading.value = true;

  try {
    const result = await omniaSdk.auth.user.register({
      ...values,
      captcha: captchaToken.value,
      ...createDemoRequest("registration", values, props.linkConfig),
      privacy_policy_last_acceptance: true,
      user_policy_last_acceptance: true,
      has_newsletter: true,
      has_marketing: values["newsletter_accepted"],
    } as UserRegister & DemoRequestRegister);

    if ("type" in result) {
      successToast(
        "Grazie per esserti registrato. Appena la tua utenza verrà confermata, riceverai un'email con le istruzioni per impostare la tua password"
      );
    } else if (!result.is_verified) {
      successToast(
        "Grazie per esserti registrato. Appena la tua utenza verrà confermata, riceverai un'email con le istruzioni per impostare la tua password"
      );
    } else {
      successToast(
        "Grazie per esserti registrato. Riceverai a breve un'email con le istruzioni per impostare la tua password"
      );
    }

    open.value = false;
  } catch (error) {
    loading.value = false;

    if (error instanceof BadRequestError) {
      const errorData = JSON.parse(error.message) as ValidationErrorMessage;

      if ("email" in errorData.errors) {
        alertToast(
          "Attenzione",
          "Esiste già un utente con questa email. Effettua l'accesso o recupera la password"
        );
        emit("requestLogin");

        return;
      }
    }

    processError(error);
  }

  loading.value = false;
}
</script>

<template>
  <Dialog v-model:open="open">
    <DialogScrollContent class="max-w-3xl" @pointer-down-outside.prevent>
      <DialogHeader>
        <DialogTitle>Iscriviti a Progetto Omnia</DialogTitle>
        <DialogDescription />
      </DialogHeader>
      <AutoForm
        :form="form"
        :schema="formSchema"
        :field-config="fieldConfig"
        class="content-card-grid grid gap-4"
        @submit="onSubmit"
      >
        <template #email="slotProps">
          <AutoFormField v-bind="slotProps" />
          <Alert v-if="emailAlreadyExists" variant="info">
            <TriangleAlert class="w-4 h-4" />
            <AlertTitle>Email già registrata</AlertTitle>
            <AlertDescription>
              <p>
                Esiste già un utente con questa email.
                <Button
                  type="button"
                  class="text-sm"
                  variant="link"
                  @click="emit('requestLogin')"
                >
                  Effettua l'accesso o recupera la password.
                </Button>
              </p>
            </AlertDescription>
          </Alert>
        </template>

        <Alert variant="warning">
          <TriangleAlert class="w-4 h-4" />
          <AlertTitle>Attenzione</AlertTitle>
          <AlertDescription class="space-y-2">
            <p>
              <strong>
                Per poterti registrare all portale, dovrai utilizzare un
                indirizzo email istituzionale. Qualora non fosse possibile,
                contatta l'Ufficio Clienti all'indirizzo
                <a href="mailto:info@gruppogaspari.it">info@gruppogaspari.it</a
                >.
              </strong>
            </p>
          </AlertDescription>
        </Alert>

        <div class="text-center">
          <NuxtTurnstile v-model="captchaToken" :options="{ theme: 'light' }" />
        </div>
        <DialogFooter class="mt-4">
          <DialogClose as-child>
            <Button type="reset" variant="ghost">Chiudi</Button>
          </DialogClose>
          <Button
            type="submit"
            :disabled="loading || !form.meta.value.valid"
            :class="{ 'animate-pulse': loading }"
          >
            Registrati
          </Button>
        </DialogFooter>
      </AutoForm>
    </DialogScrollContent>
  </Dialog>
</template>
